import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { getNotificationsClient } from "../services";
import { useNotificationsStore } from "../stores/notificationsStore";
import {
  NOTIFICATION_TYPES,
  type BrokerJoinedPayload,
  type LicenseActivatedPayload,
  type SeatsLowPayload,
  type SupportRepliedPayload,
} from "../types";

/**
 * Liga o lifecycle de autenticação ao cliente SSE de notificações.
 * Deve ser montado uma única vez no AppLayout (acima das rotas autenticadas).
 */
export function useNotificationsConnection(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const startedFor = useRef<boolean>(false);

  useEffect(() => {
    const client = getNotificationsClient();
    const store = useNotificationsStore.getState();

    client.onStatusChange((status) => {
      useNotificationsStore.getState().setConnectionStatus(status);
    });

    if (!isAuthenticated) {
      if (startedFor.current) {
        void client.stop();
        store.clear();
        startedFor.current = false;
      }
      return;
    }

    if (startedFor.current) return;
    startedFor.current = true;

    client.build(() => useAuthStore.getState().accessToken);

    // Handlers tipados
    client.on<"BrokerJoined">("BrokerJoined", (payload: BrokerJoinedPayload) => {
      useNotificationsStore.getState().addNotification({ type: "BrokerJoined", payload });
      toast.success(`${payload.brokerName} entrou para a sua equipe`);
    });

    client.on<"LicenseActivated">(
      "LicenseActivated",
      (payload: LicenseActivatedPayload) => {
        useNotificationsStore
          .getState()
          .addNotification({ type: "LicenseActivated", payload });
        toast.success(`Licença ativada para ${payload.brokerName}`);
      },
    );

    client.on<"SeatsLow">("SeatsLow", (payload: SeatsLowPayload) => {
      useNotificationsStore.getState().addNotification({ type: "SeatsLow", payload });
      toast.warning(
        `Restam ${payload.availableSlots} seat(s) no plano ${payload.planName}`,
      );
    });

    client.on<"SupportReplied">(
      "SupportReplied",
      (payload: SupportRepliedPayload) => {
        useNotificationsStore
          .getState()
          .addNotification({ type: "SupportReplied", payload });
        toast.info(`Suporte respondeu o chamado ${payload.ticketCode}`);
      },
    );

    client.start().catch((err) => {
      console.error("[notifications] failed to start client:", err);
    });

    return () => {
      // Em StrictMode o effect roda 2x; o flag startedFor evita rebuild.
      // No unmount real (logout em outra aba, etc) deixamos limpar.
      if (!useAuthStore.getState().isAuthenticated) {
        for (const ev of NOTIFICATION_TYPES) client.off(ev);
        void client.stop();
        useNotificationsStore.getState().clear();
        startedFor.current = false;
      }
    };
  }, [isAuthenticated]);
}
